import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
    AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { bolehUbahSaklarSinkronStase } from "@/lib/roles";
import { formatFullWIB } from "@/lib/datetime";
import { jumlahBerubah, teksJalan } from "@/lib/sinkronStase";
import SinkronStasePratinjau from "@/components/SinkronStasePratinjau";

// Kartu Sinkronisasi Stase di halaman Rotasi Klinik (Super Admin & Admin UFP; saklar hanya Super Admin).
export default function SinkronStaseKartu({ role }) {
    const [status, setStatus] = useState(null);
    const [gagalMuat, setGagalMuat] = useState(false);
    const [pratinjau, setPratinjau] = useState(null);
    const [sibuk, setSibuk] = useState(false);
    const [konfirmasiSaklar, setKonfirmasiSaklar] = useState(null);   // nilai saklar tujuan
    const [langkahJalan, setLangkahJalan] = useState(0);              // 0 tertutup, 1 & 2 = konfirmasi dua langkah
    const berjalan = useRef(false);
    const bolehSaklar = bolehUbahSaklarSinkronStase(role);

    const muatStatus = async () => {
        try {
            const { data } = await api.get("/sinkron-stase/status");
            setStatus(data);
            setGagalMuat(false);
        } catch {
            setGagalMuat(true);
        }
    };
    useEffect(() => { muatStatus(); }, []);

    const denganPenjaga = async (kerja) => {
        if (berjalan.current) return;
        berjalan.current = true;
        setSibuk(true);
        try {
            await kerja();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            berjalan.current = false;
            setSibuk(false);
        }
    };

    const muatPratinjau = async () => {
        const { data } = await api.get("/sinkron-stase/pratinjau");
        setPratinjau(data);
        return data;
    };
    const lihatPratinjau = () => denganPenjaga(muatPratinjau);
    const mulaiJalan = () => denganPenjaga(async () => { await muatPratinjau(); setLangkahJalan(1); });
    const jalankan = () => {
        setLangkahJalan(0);
        return denganPenjaga(async () => {
            const { data } = await api.post("/sinkron-stase/jalankan");
            toast.success(`Selesai: ${data.diubah} Stase diubah`);
            await muatStatus();
            await muatPratinjau();
        });
    };
    const ubahSaklar = () => {
        const tujuan = konfirmasiSaklar;
        setKonfirmasiSaklar(null);
        return denganPenjaga(async () => {
            await api.put("/sinkron-stase/saklar", { aktif: tujuan });
            toast.success(tujuan ? "Sinkronisasi Stase dinyalakan" : "Sinkronisasi Stase dimatikan");
            await muatStatus();
        });
    };

    if (gagalMuat) {
        return (
            <Card className="border-slate-200 shadow-sm" data-testid="sinkron-stase-kartu">
                <CardContent className="p-4 text-sm text-slate-500">Status Sinkronisasi Stase tidak dapat dimuat.</CardContent>
            </Card>
        );
    }
    if (!status) return null;

    const aktif = !!status.aktif;
    const jalanTerakhir = teksJalan(status.jalan_terakhir);
    const perubahanTerakhir = teksJalan(status.perubahan_terakhir);
    const berubah = jumlahBerubah(pratinjau?.total);

    return (
        <Card className="border-slate-200 shadow-sm" data-testid="sinkron-stase-kartu">
            <CardContent className="p-4 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-sm font-bold text-slate-900">Sinkronisasi Stase dari Rotasi</h2>
                            <Badge variant="outline" className="text-[10px]" data-testid="sinkron-stase-status">{aktif ? "Hidup" : "Mati"}</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                            Stase peserta Aktif berpindah antara Stase RSCM dan Stase Luar mengikuti entri Stase di Rotasi Klinik yang mencakup hari ini.
                        </p>
                    </div>
                    {bolehSaklar && (
                        <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 shrink-0">
                            <Switch checked={aktif} disabled={sibuk} onCheckedChange={setKonfirmasiSaklar} data-testid="sinkron-stase-saklar"
                                aria-label="Saklar Sinkronisasi Stase" />
                            Saklar
                        </label>
                    )}
                </div>

                <p className="text-xs text-slate-600" data-testid="sinkron-stase-jadwal">
                    {status.jalan_pertama_selesai
                        ? `Jadwal otomatis berjalan tiap ${status.jadwal_jam} jam selama saklar hidup.`
                        : `Jadwal otomatis (tiap ${status.jadwal_jam} jam) baru berjalan setelah satu kali "Jalankan sekarang" selesai.`}
                </p>
                {status.terakhir_dicek && (
                    <p className="text-xs text-slate-600" data-testid="sinkron-stase-terakhir-dicek">Terakhir dicek: {formatFullWIB(status.terakhir_dicek)}</p>
                )}
                {jalanTerakhir && <p className="text-xs text-slate-600" data-testid="sinkron-stase-terakhir">Jalan terakhir: {jalanTerakhir}</p>}
                {perubahanTerakhir && perubahanTerakhir !== jalanTerakhir && (
                    <p className="text-xs text-slate-600" data-testid="sinkron-stase-perubahan-terakhir">Perubahan terakhir: {perubahanTerakhir}</p>
                )}

                <div className="flex flex-wrap gap-2">
                    <Button type="button" size="sm" variant="outline" disabled={sibuk} onClick={lihatPratinjau} data-testid="sinkron-stase-pratinjau">
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Pratinjau
                    </Button>
                    <Button type="button" size="sm" className="bg-[#267B84] hover:bg-[#1F666E] text-white" disabled={sibuk || !aktif}
                        onClick={mulaiJalan} data-testid="sinkron-stase-jalankan">
                        Jalankan sekarang
                    </Button>
                    {pratinjau && (
                        <Button type="button" size="sm" variant="ghost" onClick={() => setPratinjau(null)} data-testid="sinkron-stase-tutup-pratinjau">
                            Tutup pratinjau
                        </Button>
                    )}
                    {!aktif && <span className="text-xs text-slate-500 self-center">Hanya bisa dijalankan saat saklar hidup.</span>}
                </div>

                {pratinjau && <SinkronStasePratinjau data={pratinjau} />}
            </CardContent>

            <AlertDialog open={konfirmasiSaklar !== null} onOpenChange={(b) => { if (!b) setKonfirmasiSaklar(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{konfirmasiSaklar ? "Nyalakan Sinkronisasi Stase?" : "Matikan Sinkronisasi Stase?"}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {konfirmasiSaklar
                                ? "Saklar saja belum mengubah Stase. Perubahan pertama terjadi saat \"Jalankan sekarang\" ditekan."
                                : "Jadwal dan \"Jalankan sekarang\" berhenti. Perubahan yang sudah terjadi tidak dibatalkan."}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={ubahSaklar} data-testid="sinkron-stase-saklar-ya">
                            {konfirmasiSaklar ? "Ya, nyalakan" : "Ya, matikan"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={langkahJalan > 0} onOpenChange={(b) => { if (!b) setLangkahJalan(0); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{langkahJalan === 1 ? "Periksa dulu" : "Jalankan sekarang?"}</AlertDialogTitle>
                        <AlertDialogDescription data-testid="sinkron-stase-konfirmasi-teks">
                            {langkahJalan === 1
                                ? `Menurut pratinjau hari ini, ${berubah} Stase peserta akan berubah. Tagihan Unit Cost bulan berjalan ikut menyesuaikan.`
                                : `${berubah} Stase akan diubah sekarang dan tercatat di Audit Log.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        {langkahJalan === 1 ? (
                            <Button type="button" onClick={() => setLangkahJalan(2)} data-testid="sinkron-stase-lanjut">Lanjut</Button>
                        ) : (
                            <AlertDialogAction onClick={jalankan} data-testid="sinkron-stase-ya">Ya, jalankan</AlertDialogAction>
                        )}
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}
